import React from 'react';
import { useParent } from '../context/ParentContext';
import { deriveStudentSummary, deriveOverallStatus, deriveSummaryText } from '../utils/summaryDerivation';
import { OverallStatusCard, AssignmentCompletionCard, UpcomingWorkCard, StudyEngagementCard } from '../components/cards/SummaryCards';
import CourseHealthCard from '../components/cards/CourseHealthCard';
import ParentDashboardSkeleton from '../components/states/ParentDashboardSkeleton';
import ParentErrorState from '../components/states/ParentErrorState';
import ParentEmptyState from '../components/states/ParentEmptyState';
import ParentBackendPendingBanner from '../components/ParentBackendPendingBanner';
import { FiActivity, FiClock } from 'react-icons/fi';
import '../components/cards/SummaryCards.css';
import '../components/cards/CourseHealthCard.css';
import './ParentOverview.css';

function ParentOverview() {
  const {
    selectedStudent,
    currentCourses,
    currentAssignments,
    currentActivity,
    hasActivityFeedBackend,
    hasAlertsBackend,
    demoState,
    loading,
    error,
    refreshChildren,
    setDemoState,
  } = useParent();

  // Explicit demo overrides come first (dev tooling).
  if (demoState === 'loading') return <ParentDashboardSkeleton />;
  if (demoState === 'error') {
    return <ParentErrorState variant="load-failed" onRetry={() => setDemoState('normal')} />;
  }
  if (demoState === 'empty') return <ParentEmptyState variant="no-students" />;

  // Live loading / error states from the real provider.
  if (loading && !selectedStudent) return <ParentDashboardSkeleton />;
  if (error) return <ParentErrorState variant="load-failed" onRetry={refreshChildren} />;
  if (!selectedStudent) return <ParentEmptyState variant="no-students" />;

  const summary = deriveStudentSummary(currentAssignments, currentCourses);
  const summaryText = deriveSummaryText(selectedStudent, summary);
  const overallStatus = deriveOverallStatus(summary);

  const overdueAssignments = currentAssignments.filter(a => a.status === 'overdue');
  const upcomingAssignments = currentAssignments.filter(a => ['not-started', 'in-progress'].includes(a.status));
  const dueThisWeek = upcomingAssignments.filter(a => {
    if (!a.dueDate) return false;
    const due = new Date(a.dueDate);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return due <= nextWeek;
  });
  const nextDeadline = upcomingAssignments
    .filter(a => !!a.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];

  return (
    <div className="parent-overview">
      <div className="parent-overview__header">
        <div className="parent-overview__greeting">
          <h1 className="parent-overview__title">
            Here's how {selectedStudent?.firstName || 'your student'} is doing this week.
          </h1>
          <span className="parent-overview__updated">
            <FiClock size={12} />
            Updated today
          </span>
        </div>
      </div>

      {summaryText && (
        <div className="parent-overview__summary-text">
          <p>{summaryText}</p>
        </div>
      )}

      <div className="summary-cards-grid">
        <OverallStatusCard
          status={overallStatus}
          description={
            overallStatus === 'on-track'
              ? 'Assignments are being completed on time.'
              : overallStatus === 'needs-attention'
              ? 'Some assignments need attention.'
              : overallStatus === 'at-risk'
              ? 'Multiple assignments are overdue.'
              : null
          }
        />
        <AssignmentCompletionCard completed={summary.completed} total={summary.total} />
        <UpcomingWorkCard
          dueThisWeek={dueThisWeek.length}
          overdue={overdueAssignments.length}
          nextDeadline={nextDeadline ? formatDate(nextDeadline.dueDate) : null}
        />
        <StudyEngagementCard
          activeDays={currentActivity.activeDays || 0}
          quizzes={currentActivity.quizzesCompleted || 0}
          flashcardSessions={currentActivity.flashcardSessions || 0}
          conversations={currentActivity.studyConversations || 0}
          notes={currentActivity.notesCreated || 0}
        />
      </div>

      <section className="parent-overview__section" data-testid="parent-alerts-section">
        <h2 className="parent-overview__section-title">Needs Your Attention</h2>
        {hasAlertsBackend ? (
          <ParentEmptyState variant="no-alerts" />
        ) : (
          <ParentBackendPendingBanner
            state="pending"
            message="Alerts backend pending."
            hint="See src/parent/BACKEND_GAPS.md."
          />
        )}
      </section>

      <section className="parent-overview__section">
        <h2 className="parent-overview__section-title">Upcoming Assignments</h2>
        {upcomingAssignments.length > 0 ? (
          <div className="parent-overview__assignments">
            {upcomingAssignments.slice(0, 5).map(assignment => (
              <div key={assignment.id} className="parent-overview__assignment-row">
                <div className="parent-overview__assignment-info">
                  <span className="parent-overview__assignment-title">{assignment.title}</span>
                  <span className="parent-overview__assignment-course">{assignment.course}</span>
                </div>
                <div className="parent-overview__assignment-meta">
                  <span className={`parent-overview__assignment-status parent-overview__assignment-status--${assignment.status}`}>
                    {formatStatus(assignment.status)}
                  </span>
                  <span className="parent-overview__assignment-due">
                    {assignment.dueDate ? formatDate(assignment.dueDate) : '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ParentEmptyState variant="no-assignments" />
        )}
      </section>

      <section className="parent-overview__section">
        <h2 className="parent-overview__section-title">Course Health</h2>
        {currentCourses.length > 0 ? (
          <div className="course-health-grid">
            {currentCourses.map(course => (
              <CourseHealthCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <ParentEmptyState variant="no-data" />
        )}
      </section>

      <section className="parent-overview__section" data-testid="parent-activity-feed-section">
        <h2 className="parent-overview__section-title">
          <FiActivity size={16} />
          Recent Activity
        </h2>
        {hasActivityFeedBackend ? (
          <ParentEmptyState variant="no-activity" />
        ) : (
          <ParentBackendPendingBanner
            state="pending"
            message="Activity feed backend pending."
            hint="See src/parent/BACKEND_GAPS.md."
          />
        )}
      </section>
    </div>
  );
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const diff = Math.round((date - today) / (1000 * 60 * 60 * 24));

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff <= 7) return `In ${diff} days`;
  if (diff < 0) return `${Math.abs(diff)} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatStatus(status) {
  const labels = {
    'not-started': 'Not Started',
    'in-progress': 'In Progress',
    'ready-to-submit': 'Ready to Submit',
    'completed': 'Completed',
    'submitted': 'Submitted',
    'overdue': 'Overdue',
  };
  return labels[status] || status;
}

export default ParentOverview;
